/**
 * Das Anlagenschema eines Messkonzepts — `KonzeptSkizze` aus
 * `ui_kits/dashboard/MesskonzeptScreen.jsx` (HM-012, nach dem Entwurf neu
 * gezeichnet mit HM-114).
 *
 * **Die Skizze ist die Hauptbedienfläche, nicht nur ein Bild.** Ein Steckplatz
 * wird dort angeklickt, wo er im Haus sitzt; die Liste unter der Karte ist der
 * Ausweg für die Tastatur. Gezeichnet wird aus derselben Rechnung, aus der die
 * Liste ihre Zeilen bildet (`conceptStructure`), damit beides nie
 * auseinanderläuft.
 *
 * **Ein Steckplatz trägt genau einen Zähler.** Rollen, die mehrere tragen —
 * Wohnungszähler, Erzeugungszähler, Verbraucherzähler —, sind ein senkrechter
 * Strang mit einem Abgang je Zähler und einem Plus darunter. Mehrere Zähler
 * einer Rolle liegen **parallel**, nicht in Reihe: zwei Kästen übereinander an
 * einer durchgehenden Linie hießen Reihenschaltung. Die Zusammenfassung
 * „6 · 4 zugeordnet" aus HM-080 ist entfallen — dort stand eine Zahl, wo der
 * Nutzer einen Namen sucht. Die Skizze wächst stattdessen nach unten.
 *
 * **Drei Ränder sagen den Zustand:** gestrichelt ist offen — in Warnfarbe, wenn
 * der Steckplatz Pflicht ist —, durchgezogen ist belegt, grün und hinterlegt
 * ist, was gerade gewählt ist.
 *
 * **Was hinter etwas hängt, steht als Kasten „Verbraucher" darunter** — gleiche
 * Form hinter einer Wohneinheit und hinter „Verbrauch im Haus", weil es
 * dieselbe Sache ist: eine Aufschlüsselung, die die Bilanz nicht verändert.
 * Unter einem **Erzeugungszähler** steht seit HM-122 der Kasten
 * „Erzeugungsanlagen" (ADR 0038): die PV-Anlagen und Speicher hinter seiner
 * Messstelle — eine Aufschlüsselung, die die Bilanz nicht verändert, wie die
 * Verbraucher. Er wird nur gezeichnet, wenn der Aufrufer `assetsOf` gibt, und
 * ein Klick **hinterlegt sie dort** (HM-124), genau wie ein Verbraucher hinter
 * seinem Zähler hinterlegt wird. Ein Speicher steht seit #40 immer dort — einen
 * eigenen Speicherzähler im Messkonzept gibt es nicht mehr.
 *
 * **Bei „mit Eigenverbrauch" hängen unter dem Zähler der selbst genutzten
 * Einheit zwei Dinge parallel**, an einem Strang wie bei Mehrfachrollen: der
 * Erzeugungszähler — der Pfeil zeigt zur Einheit, denn die Erzeugung läuft
 * zuerst in sie und erst der Rest ins Haus — und der Kasten „Verbraucher ·
 * Einheit" mit ihren Verbrauchern. Untereinander an einer Linie sähen sie aus
 * wie eine Reihe.
 *
 * Der Zeichenraum ist bewusst schmal (620 Einheiten): Die Skizze steht in einer
 * Spalte von rund 580 px, ein breiterer Raum drückte die Beschriftung unter die
 * Lesbarkeitsgrenze. Ein reines SVG ohne Bibliothek: kein Diagramm im Sinne von
 * ADR 0021, sondern ein Bild aus Kästen und Linien.
 */
import type { ReactNode } from "react";
import type { ApiComponents } from "@hausmeter/sdk";
import { consumersBehindHouse } from "../lib/concept-sketch-rest.js";
import { conceptStructure, isAssetSlot, type ConceptRole } from "../lib/concept-roles.js";

type Template = ApiComponents["schemas"]["ConceptTemplate"];
type Slot = ApiComponents["schemas"]["ConceptTemplateSlot"];

/** Der Schlüssel, unter dem „Verbrauch im Haus" gewählt ist — kein Steckplatz heißt so. */
export const HOUSE_KEY = "$haus";
export const behindKey = (unitSlotKey: string): string => `$dahinter:${unitSlotKey}`;
export const addKey = (roleKey: string): string => `$plus:${roleKey}`;

/** Was ein Klick in die Skizze meint. */
export type SketchPick =
  | { readonly kind: "slot"; readonly key: string; readonly slot: string }
  | { readonly kind: "house"; readonly key: string }
  | { readonly kind: "behind"; readonly key: string; readonly unitSlot: string }
  | { readonly kind: "add"; readonly key: string; readonly role: string }
  /** Die Erzeugungsanlagen hinter einem Erzeugungszähler (HM-122, hinterlegt seit HM-124). */
  | { readonly kind: "assets"; readonly key: string; readonly slot: string };

export const assetsKey = (slotKey: string): string => `$anlagen:${slotKey}`;

const INK = "var(--border-strong)";
const NAME = {
  fontFamily: "var(--font-body)",
  fontSize: 11.5,
  fontWeight: 700,
  fill: "var(--text-strong)",
} as const;
const ROLE = {
  fontFamily: "var(--font-body)",
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: ".06em",
  fill: "var(--text-faint)",
} as const;
const FLOW = { fontFamily: "var(--font-body)", fontSize: 9.5, fill: "var(--text-muted)" } as const;

const BUS_Y = 130;
const TOP = 181;
const STEP = 58;
const BEHIND = 42;
const CHILD = 76;

function clip(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, Math.max(1, max - 1))}…` : text;
}

export function ConceptSketch({
  template,
  meterNameOf,
  assetsOf,
  activeKey = null,
  onPick,
  readOnly = false,
}: {
  template: Template;
  /** Der Name des Zählers im Steckplatz — `null`, wenn keiner steckt. */
  meterNameOf: (slot: Slot) => string | null;
  /**
   * Die Erzeugungsanlagen hinter einem Erzeugungszähler (HM-122, #40), leer
   * wenn dort noch keine steht. **Ob der Kasten gezeichnet wird, entscheidet
   * die Skizze** (`isAssetSlot`) und nicht der Aufrufer: Sonst hängt die
   * Form des Bildes daran, was der Aufrufer über die Daten weiß — und ein
   * Steckplatz ohne Zähler verlor seinen Kasten (HM-125). Fehlt die Funktion
   * ganz, gibt es keinen Kasten.
   */
  assetsOf?: (slot: Slot) => readonly string[];
  /** Was gerade gewählt ist — der Schlüssel aus `SketchPick`. */
  activeKey?: string | null;
  onPick?: (pick: SketchPick) => void;
  /**
   * Nur ansehen (HM-160, Regel 10): Ohne Schreibrecht fehlt das Plus, und ein
   * leerer Steckplatz ist kein Knopf — es gibt dort nichts anzusehen.
   */
  readOnly?: boolean;
}) {
  const structure = conceptStructure(template);
  const { connection, separate, branches, ownChildren, behindUnit, houseConsumers, houseRest } =
    structure;
  const connectionSlot = connection?.slots[0];
  if (connectionSlot === undefined) return null;
  const behindHouse = consumersBehindHouse(template);
  const consumerUnits = template.layout?.consumerUnits === true;

  /* Die Abzweige: die Rollen und, wo es einen ungemessenen Rest gibt, der
     Verbrauch im Haus. */
  type Drop = { readonly kind: "role"; readonly role: ConceptRole } | { readonly kind: "house" };
  const drops: Drop[] = [
    ...branches.map((role): Drop => ({ kind: "role", role })),
    ...(houseRest ? [{ kind: "house" } as const] : []),
  ];
  const count = Math.max(drops.length, 1);
  const colW = 620 / count;
  const xs =
    drops.length === 1
      ? [310]
      : drops.length === 2
        ? [160, 460]
        : drops.length === 3
          ? [100, 310, 520]
          : drops.map((_, index) => colW * (index + 0.5));
  const W1 = Math.min(186, colW - 14);
  const WN = Math.min(160, colW - 44);

  const interactive = (
    pick: SketchPick,
    label: string,
    children: ReactNode,
    key: string,
    pickable = true,
  ) => {
    const enabled = onPick !== undefined && pickable;
    return (
      <g
        key={key}
        onClick={enabled ? () => onPick(pick) : undefined}
        onKeyDown={(event) => {
          if (enabled && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            onPick(pick);
          }
        }}
        role={enabled ? "button" : undefined}
        tabIndex={enabled ? 0 : undefined}
        aria-pressed={enabled ? activeKey === pick.key : undefined}
        aria-label={enabled ? label : undefined}
        style={enabled ? { cursor: "pointer" } : undefined}
      >
        {children}
      </g>
    );
  };

  /** Ein Steckplatz: offen (gestrichelt), belegt (durchgezogen), gewählt (grün und angehoben). */
  const chip = (cx: number, cy: number, slot: Slot, w: number, roleLabel = slot.label) => {
    const name = meterNameOf(slot);
    const on = activeKey === slot.key;
    const open = name === null;
    const nameText =
      name ?? (slot.required ? (readOnly ? "kein Zähler" : "zuordnen") : "optional · leer");
    return interactive(
      { kind: "slot", key: slot.key, slot: slot.key },
      `${roleLabel}: ${name ?? (slot.required ? "kein Zähler, Pflicht" : "kein Zähler, optional")}`,
      <>
        {on && (
          <rect
            x={cx - w / 2 - 3}
            y={cy - 28}
            width={w + 6}
            height={56}
            rx={12}
            fill="var(--hm-green-500)"
            opacity={0.14}
          />
        )}
        <rect
          x={cx - w / 2}
          y={cy - 25}
          width={w}
          height={50}
          rx={9}
          fill={on ? "var(--hm-green-50)" : "var(--surface-card)"}
          stroke={
            on
              ? "var(--hm-green-500)"
              : open && slot.required
                ? "var(--hm-warning)"
                : "var(--border-default)"
          }
          strokeWidth={on ? 2 : 1}
          strokeDasharray={open ? "4 3" : undefined}
        />
        <text x={cx} y={cy - 6} textAnchor="middle" style={ROLE}>
          {clip(roleLabel.toUpperCase(), Math.floor(w / 6.2))}
        </text>
        <text
          x={cx}
          y={cy + 12}
          textAnchor="middle"
          style={
            open
              ? { ...NAME, fill: slot.required ? "var(--hm-warning)" : "var(--text-faint)" }
              : NAME
          }
        >
          {clip(nameText, Math.floor(w / 7.4))}
        </text>
      </>,
      `chip:${slot.key}`,
      !(readOnly && open),
    );
  };

  /**
   * Der Kasten „Verbraucher" — dieselbe Form unter einer Wohneinheit und unter
   * dem Verbrauch im Haus.
   *
   * **Er hieß anfangs „dahinter"**, und das benannte die Beziehung statt der
   * Sache: Was dort hängt, ist ein Verbraucher mit eigenem Zähler — eine
   * Wallbox, eine Wärmepumpe. Dass er *hinter* etwas hängt, sagt schon die
   * Linie, an der er sitzt. Die Aufschlüsselung an einem Erzeugungszähler, für
   * die „dahinter" ebenfalls gepasst hätte, gibt es nicht (OQ-040).
   */
  const behindBody = (
    bx: number,
    by: number,
    bw: number,
    slots: readonly Slot[],
    on: boolean,
    tag = "VERBRAUCHER",
  ) => {
    const missing = slots.some((slot) => meterNameOf(slot) === null);
    /* Bei mehreren zählt der Kasten die Steckplätze, statt sein eigenes Wort zu
       wiederholen: Unter „VERBRAUCHER" stünde sonst „3 Verbraucher". */
    const words =
      slots.length === 0
        ? "hinterlegen"
        : slots.length === 1
          ? clip(slots[0]?.label ?? "", Math.floor(bw / 6))
          : `${slots.length} Steckplätze`;
    return (
      <>
        <rect
          x={bx}
          y={by}
          width={bw}
          height={30}
          rx={8}
          fill={on ? "var(--hm-green-50)" : "var(--surface-inset)"}
          stroke={
            on ? "var(--hm-green-500)" : missing ? "var(--hm-warning)" : "var(--border-subtle)"
          }
          strokeWidth={on ? 2 : 1}
          strokeDasharray={slots.length === 0 || missing ? "4 3" : undefined}
        />
        <text x={bx + bw / 2} y={by + 12} textAnchor="middle" style={{ ...ROLE, fontSize: 8.5 }}>
          {tag}
        </text>
        {slots.length === 0 ? (
          <>
            <path
              d={`M${bx + 20} ${by + 21}h9M${bx + 24.5} ${by + 16.5}v9`}
              stroke="var(--text-muted)"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <text
              x={bx + bw / 2 + 10}
              y={by + 24}
              textAnchor="middle"
              style={{ ...FLOW, fontWeight: 600 }}
            >
              {words}
            </text>
          </>
        ) : (
          <text
            x={bx + bw / 2}
            y={by + 24}
            textAnchor="middle"
            style={{
              ...FLOW,
              fontWeight: 600,
              fill: missing ? "var(--hm-warning)" : "var(--text-body)",
            }}
          >
            {words}
          </text>
        )}
      </>
    );
  };

  const assetNames = (slot: Slot): readonly string[] | null =>
    assetsOf === undefined || !isAssetSlot(slot) ? null : assetsOf(slot);

  /**
   * **Jeder Erzeugungs-Steckplatz trägt den Kasten**, gleich wo er in der
   * Skizze sitzt: am Abzweig, im Strang, unter der selbst genutzten Einheit
   * („mit Eigenverbrauch") und am eigenen Zählpunkt (Volleinspeisung). Das war
   * bis HM-125 nicht so — `ownColumn` und der Zählpunkt zeichnen ihre Kästen
   * selbst, und dort fehlte er; in einem Mieterstromhaus mit Eigenverbrauch
   * gab es damit **keinen Ort**, an dem sich eine PV-Anlage zuordnen ließ.
   */
  const assetsUnder = (cx: number, cy: number, slot: Slot, w: number) =>
    assetNames(slot) === null ? null : assetsBox(cx, cy, slot, w);

  /** Ob unter einem Steckplatz ein Kasten hängt — die Verbraucher einer Wohneinheit oder die Anlagen hinter einem Erzeugungszähler. */
  const hasBox = (slot: Slot): boolean =>
    (behindUnit.has(slot.key) && ((behindUnit.get(slot.key)?.length ?? 0) > 0 || consumerUnits)) ||
    assetNames(slot) !== null;

  /**
   * Der Kasten „Erzeugungsanlagen" unter einem Erzeugungszähler (HM-122):
   * dieselbe Form wie „Verbraucher", ein anderer Gegenstand. Ohne Anlage
   * gestrichelt mit „hinterlegen" — der Weg führt zu den Erzeugungsanlagen.
   */
  const assetsBox = (cx: number, cy: number, slot: Slot, w: number) => {
    const names = assetNames(slot);
    if (names === null) return null;
    const key = assetsKey(slot.key);
    const bw = Math.min(w - 16, 152);
    const on = activeKey === key;
    const words =
      names.length === 0
        ? "hinterlegen"
        : names.length === 1
          ? clip(names[0] ?? "", Math.floor(bw / 6))
          : `${names.length} Anlagen`;
    return interactive(
      { kind: "assets", key, slot: slot.key },
      `Hinter ${slot.label}: ${names.length === 0 ? "keine Erzeugungsanlage, hinterlegen" : names.join(", ")}`,
      <>
        <path
          d={`M${cx} ${cy + 25}V${cy + 31}`}
          stroke={INK}
          strokeWidth="1.5"
          strokeDasharray={names.length === 0 ? "3 3" : undefined}
          fill="none"
        />
        <rect
          x={cx - bw / 2}
          y={cy + 31}
          width={bw}
          height={30}
          rx={8}
          fill={on ? "var(--hm-green-50)" : "var(--surface-inset)"}
          stroke={on ? "var(--hm-green-500)" : "var(--border-subtle)"}
          strokeWidth={on ? 2 : 1}
          strokeDasharray={names.length === 0 ? "4 3" : undefined}
        />
        <text x={cx} y={cy + 43} textAnchor="middle" style={{ ...ROLE, fontSize: 8.5 }}>
          ERZEUGUNGSANLAGEN
        </text>
        <text
          x={cx}
          y={cy + 55}
          textAnchor="middle"
          style={{ ...FLOW, fontWeight: 600, fill: "var(--text-body)" }}
        >
          {words}
        </text>
      </>,
      key,
    );
  };

  const behindBox = (cx: number, cy: number, slot: Slot, w: number) => {
    if (assetNames(slot) !== null) return assetsBox(cx, cy, slot, w);
    if (!hasBox(slot)) return null;
    const slots = behindUnit.get(slot.key) ?? [];
    const key = behindKey(slot.key);
    const bw = Math.min(w - 16, 152);
    return interactive(
      { kind: "behind", key, unitSlot: slot.key },
      `Hinter ${slot.label}: ${slots.length === 0 ? "kein Verbraucher, hinterlegen" : slots.map((entry) => entry.label).join(", ")}`,
      <>
        <path
          d={`M${cx} ${cy + 25}V${cy + 31}`}
          stroke={INK}
          strokeWidth="1.5"
          strokeDasharray={slots.length === 0 ? "3 3" : undefined}
          fill="none"
        />
        {behindBody(cx - bw / 2, cy + 31, bw, slots, activeKey === key)}
      </>,
      key,
    );
  };

  /** Das Plus legt einen weiteren Steckplatz derselben Rolle an. Es hängt am selben Strang. */
  const addPill = (cx: number, cy: number, role: ConceptRole, w: number) => {
    const key = addKey(role.key);
    const on = activeKey === key;
    const text =
      role.family === "units"
        ? "Wohnungszähler"
        : role.family === "consumers"
          ? "Verbraucherzähler"
          : "Erzeugungszähler";
    return interactive(
      { kind: "add", key, role: role.key },
      `${text} hinzufügen`,
      <>
        <rect
          x={cx - w / 2}
          y={cy - 14}
          width={w}
          height={28}
          rx={14}
          fill={on ? "var(--hm-green-50)" : "var(--surface-inset)"}
          stroke={on ? "var(--hm-green-500)" : "var(--border-default)"}
          strokeWidth={on ? 2 : 1}
          strokeDasharray="4 3"
        />
        <path
          d={`M${cx - w / 2 + 14} ${cy}h11M${cx - w / 2 + 19.5} ${cy - 5.5}v11`}
          stroke="var(--text-muted)"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
        <text x={cx + 12} y={cy + 3.5} textAnchor="middle" style={{ ...FLOW, fontWeight: 600 }}>
          {clip(text, Math.floor((w - 30) / 5.4))}
        </text>
      </>,
      key,
    );
  };

  /**
   * Ein Strang: senkrechte Linie mit einem Abgang je Steckplatz und dem Plus.
   * Die Höhe eines Platzes wächst, wenn ein Kasten darunter hängt; darum werden
   * die Y-Werte aufaddiert statt aus einem festen Raster gerechnet.
   */
  const strand = (
    riser: number,
    chipCx: number,
    from: number,
    top: number,
    role: ConceptRole,
    w: number,
  ) => {
    const ys: number[] = [];
    let y = top;
    for (const slot of role.slots) {
      ys.push(y);
      y += STEP + (hasBox(slot) ? BEHIND : 0);
    }
    const last = role.slots[role.slots.length - 1];
    const lastY = ys[ys.length - 1] ?? top;
    const lastBottom = last === undefined ? top - 25 : lastY + 25 + (hasBox(last) ? BEHIND : 0);
    const pillY = role.add === null || readOnly ? null : last === undefined ? top : lastBottom + 21;
    const end = pillY ?? lastY;
    const bottom = pillY === null ? lastBottom : pillY + 14;
    const pillW = Math.min(142, w - 18);
    const node = (
      <g key={`strand:${role.key}`}>
        <path d={`M${riser} ${from}V${end}`} stroke={INK} strokeWidth="1.5" fill="none" />
        {role.slots.map((slot, index) => {
          const cy = ys[index] ?? top;
          return (
            <g key={`branch:${slot.key}`}>
              <path
                d={`M${riser} ${cy}H${chipCx - w / 2 - 2}`}
                stroke={INK}
                strokeWidth="1.5"
                fill="none"
                markerEnd={role.up ? undefined : "url(#hmConceptArrow)"}
                markerStart={role.up ? "url(#hmConceptArrow)" : undefined}
              />
              {chip(chipCx, cy, slot, w)}
              {behindBox(chipCx, cy, slot, w)}
            </g>
          );
        })}
        {pillY !== null && (
          <>
            <path
              d={`M${riser} ${pillY}H${chipCx - pillW / 2}`}
              stroke={INK}
              strokeWidth="1.5"
              strokeDasharray="3 3"
              fill="none"
            />
            {addPill(chipCx, pillY, role, pillW)}
          </>
        )}
      </g>
    );
    return { node, bottom };
  };

  /**
   * Die selbst genutzte Einheit und was unter ihr hängt: ein Strang mit den
   * Erzeugungszählern (samt Plus, wo sich weitere anlegen lassen) und dem Kasten
   * „Verbraucher · Einheit" — parallel, nicht in Reihe.
   */
  const ownColumn = (cx: number, role: ConceptRole) => {
    const slot = role.slots[0];
    if (slot === undefined) return { node: null, bottom: TOP + 25 };
    const generators = ownChildren[0];
    const consumers = behindUnit.get(slot.key) ?? [];
    const showBox = consumers.length > 0 || consumerUnits;
    const chipCx = cx + 12;
    const width = WN;
    const edge = chipCx - width / 2;
    const riser = edge - 20;
    const parts: ReactNode[] = [];
    let y = TOP + CHILD;
    let end = TOP + 38;
    let lastHadBox = false;
    for (const child of generators?.slots ?? []) {
      lastHadBox = assetNames(child) !== null;
      parts.push(
        <g key={`own:${child.key}`}>
          <path
            d={`M${riser} ${y}H${edge - 2}`}
            stroke={INK}
            strokeWidth="1.5"
            fill="none"
            markerStart="url(#hmConceptArrow)"
          />
          {chip(chipCx, y, child, width)}
          {/* Die Erzeugungsanlagen hinter diesem Zähler (HM-125). */}
          {assetsUnder(chipCx, y, child, width)}
        </g>,
      );
      end = y;
      y += STEP + (lastHadBox ? BEHIND : 0);
    }
    let bottom = end + 25 + (lastHadBox ? BEHIND : 0);
    if (generators !== undefined && generators.add !== null && !readOnly) {
      const pillY = end + 25 + (lastHadBox ? BEHIND : 0) + 21;
      const pillW = Math.min(142, width - 10);
      parts.push(
        <g key="own:plus">
          <path
            d={`M${riser} ${pillY}H${chipCx - pillW / 2}`}
            stroke={INK}
            strokeWidth="1.5"
            strokeDasharray="3 3"
            fill="none"
          />
          {addPill(chipCx, pillY, generators, pillW)}
        </g>,
      );
      end = pillY;
      bottom = pillY + 14;
    }
    if (showBox) {
      const consCy = bottom + 29;
      const bw = Math.min(152, width);
      const key = behindKey(slot.key);
      parts.push(
        interactive(
          { kind: "behind", key, unitSlot: slot.key },
          `Hinter der selbst genutzten Einheit: ${consumers.length === 0 ? "kein Verbraucher, hinterlegen" : consumers.map((entry) => entry.label).join(", ")}`,
          <>
            <path
              d={`M${riser} ${consCy}H${chipCx - bw / 2 - 2}`}
              stroke={INK}
              strokeWidth="1.5"
              fill="none"
              markerEnd="url(#hmConceptArrow)"
            />
            {behindBody(
              chipCx - bw / 2,
              consCy - 15,
              bw,
              consumers,
              activeKey === key,
              "VERBRAUCHER · EINHEIT",
            )}
          </>,
          key,
        ),
      );
      end = consCy;
      bottom = consCy + 15;
    }
    return {
      node: (
        <g key="own">
          {chip(cx, TOP, slot, W1, "Selbst genutzte Einheit")}
          {parts.length > 0 && (
            <path
              d={`M${cx} ${TOP + 25}V${TOP + 38}H${riser}V${end}`}
              stroke={INK}
              strokeWidth="1.5"
              fill="none"
            />
          )}
          {parts}
        </g>
      ),
      bottom,
    };
  };

  const houseBox = (cx: number) => {
    const on = activeKey === HOUSE_KEY;
    const bw = Math.min(W1 - 16, 152);
    const body = (
      <>
        {on && (
          <rect
            x={cx - W1 / 2 - 3}
            y={151}
            width={W1 + 6}
            height={50}
            rx={12}
            fill="var(--hm-green-500)"
            opacity={0.14}
          />
        )}
        <rect
          x={cx - W1 / 2}
          y={154}
          width={W1}
          height={44}
          rx={9}
          fill="var(--surface-sunken)"
          stroke={on ? "var(--hm-green-500)" : "var(--border-default)"}
          strokeWidth={on ? 2 : 1}
        />
        <text x={cx} y={181} textAnchor="middle" style={NAME}>
          {W1 < 150 ? "Verbrauch" : "Verbrauch im Haus"}
        </text>
        {behindHouse && (
          <>
            <path
              d={`M${cx} 198V210`}
              stroke={INK}
              strokeWidth="1.5"
              strokeDasharray={houseConsumers.length === 0 ? "3 3" : undefined}
              fill="none"
            />
            {behindBody(cx - bw / 2, 210, bw, houseConsumers, on)}
          </>
        )}
      </>
    );
    return behindHouse ? (
      interactive(
        { kind: "house", key: HOUSE_KEY },
        `Verbrauch im Haus: ${houseConsumers.length === 0 ? "kein Verbraucher hinterlegt" : houseConsumers.map((slot) => slot.label).join(", ")}`,
        body,
        "house",
      )
    ) : (
      <g key="house">{body}</g>
    );
  };

  /* Die Schiene reicht bis zum äußersten Abgang. */
  const anchors = drops.map((drop, index) => {
    const cx = xs[index] ?? 310;
    return drop.kind === "role" && drop.role.many ? cx + 12 - WN / 2 - 20 : cx;
  });
  const busA = drops.length <= 1 ? Math.min(220, anchors[0] ?? 310) : Math.min(...anchors);
  const busB = drops.length <= 1 ? Math.max(400, anchors[0] ?? 310) : Math.max(...anchors);

  const columns = drops.map((drop, index) => {
    const cx = xs[index] ?? 310;
    const anchor = anchors[index] ?? cx;
    if (drop.kind === "house") {
      return {
        node: houseBox(cx),
        bottom: behindHouse ? 240 : 198,
        anchor,
        flow: "Verbrauch",
        up: false,
        many: false,
      };
    }
    const role = drop.role;
    if (role.family === "own_unit") {
      return {
        ...ownColumn(cx, role),
        anchor,
        flow: role.delivers[0] ?? "",
        up: false,
        many: false,
      };
    }
    if (role.many) {
      return {
        ...strand(anchor, cx + 12, BUS_Y, TOP, role, WN),
        anchor,
        flow: role.delivers[0] ?? "",
        up: role.up,
        many: true,
      };
    }
    const slot = role.slots[0];
    return {
      node:
        slot === undefined ? null : (
          <g key={`single:${role.key}`}>
            {chip(cx, TOP, slot, W1)}
            {behindBox(cx, TOP, slot, W1)}
          </g>
        ),
      bottom: TOP + 25 + (slot !== undefined && hasBox(slot) ? BEHIND : 0),
      anchor,
      flow: role.delivers[0] ?? "",
      up: role.up,
      many: false,
    };
  });

  const separateSlot = separate?.slots[0];
  const height = Math.max(248, ...columns.map((column) => column.bottom + 12));
  const twoWay = connectionSlot.delivers.length > 1;

  return (
    <svg
      viewBox={`0 0 620 ${height}`}
      width="100%"
      role="group"
      aria-label={`Anlagenschema für ${template.label}`}
      style={{ display: "block", width: "100%", maxWidth: 620, margin: "0 auto" }}
    >
      <defs>
        <marker
          id="hmConceptArrow"
          viewBox="0 0 8 8"
          refX="7"
          refY="4"
          markerWidth="5.5"
          markerHeight="5.5"
          orient="auto-start-reverse"
        >
          <path d="M0 0L8 4L0 8z" fill="var(--border-strong)" />
        </marker>
      </defs>

      {/* Netz oben, Haus darunter — der Weg des Stroms läuft von oben nach unten. */}
      <rect
        x={245}
        y={4}
        width={130}
        height={34}
        rx={9}
        fill="var(--surface-sunken)"
        stroke="var(--border-default)"
      />
      <text x={310} y={26} textAnchor="middle" style={NAME}>
        Netz
      </text>
      <path
        d="M310 38V62"
        stroke={INK}
        strokeWidth="1.5"
        fill="none"
        markerEnd="url(#hmConceptArrow)"
        markerStart={twoWay ? "url(#hmConceptArrow)" : undefined}
      />
      <text x={322} y={54} style={FLOW}>
        {twoWay ? "Bezug ⇄ Einspeisung" : (connectionSlot.delivers[0] ?? "Bezug")}
      </text>
      {chip(310, 86, connectionSlot, 190)}

      <path d="M310 111V128" stroke={INK} strokeWidth="1.5" fill="none" />
      {drops.length > 0 && (
        <>
          <path d={`M${busA} ${BUS_Y}H${busB}`} stroke={INK} strokeWidth="2.5" fill="none" />
          <text x={busA} y={122} style={ROLE}>
            HAUSVERTEILUNG
          </text>
        </>
      )}

      {columns.map((column, index) => {
        const cx = xs[index] ?? 310;
        return (
          <g key={`column:${index}`}>
            {!column.many && (
              <path
                d={`M${cx} ${BUS_Y}V${drops[index]?.kind === "house" ? 154 : 152}`}
                stroke={INK}
                strokeWidth="1.5"
                fill="none"
                markerEnd={column.up ? undefined : "url(#hmConceptArrow)"}
                markerStart={column.up ? "url(#hmConceptArrow)" : undefined}
              />
            )}
            <text x={column.anchor + (column.many ? 4 : 8)} y={148} style={FLOW}>
              {clip(column.flow, Math.floor(colW / 6))}
            </text>
            {column.node}
          </g>
        );
      })}

      {/* Volleinspeisung: eigener Zählpunkt, der am Hausverbrauch vorbeigeht. Die
          Erzeugung geschieht hinter dem Einspeisezähler; der Weg führt nach oben ins Netz. */}
      {separateSlot !== undefined && (
        <g>
          <path
            d="M100 61V21H243"
            stroke={INK}
            strokeWidth="1.5"
            fill="none"
            markerEnd="url(#hmConceptArrow)"
          />
          <text x={150} y={14} textAnchor="middle" style={FLOW}>
            vollständig ins Netz
          </text>
          {chip(100, 86, separateSlot, 172)}
          {/* Auch hier stehen die Erzeugungsanlagen dahinter (HM-125): Bei
              Volleinspeisung ist dieser Zählpunkt der Erzeugungszähler. */}
          {assetsUnder(100, 86, separateSlot, 172)}
        </g>
      )}
    </svg>
  );
}
